import type { LinkPreviewData } from '@teo/widget-shared';
import { useState } from 'react';
import { useRichMedia } from './RichMediaContext.js';

type LinkPreviewCardProps = {
  preview: LinkPreviewData;
  onMediaLoad?: () => void;
};

function LinkPreviewSkeletonBody({
  preview,
  showDescription,
  showDomain,
}: {
  preview: LinkPreviewData;
  showDescription: boolean;
  showDomain: boolean;
}) {
  return (
    <>
      {preview.siteName && (
        <span className="teo-skeleton-line teo-skeleton-line--site" />
      )}
      <span className="teo-skeleton-line teo-skeleton-line--title" />
      {showDescription && (
        <span className="teo-skeleton-line teo-skeleton-line--desc" />
      )}
      {showDomain && (
        <span className="teo-skeleton-line teo-skeleton-line--domain" />
      )}
    </>
  );
}

export function LinkPreviewCard({ preview, onMediaLoad }: LinkPreviewCardProps) {
  const { openLightbox } = useRichMedia();
  const hasImage = Boolean(preview.imageUrl);
  const isEmbeddable = Boolean(preview.embedUrl);
  const [loaded, setLoaded] = useState(!hasImage);
  const showDescription =
    Boolean(preview.description) &&
    preview.description !== preview.url &&
    preview.description !== preview.domain;
  const showDomain =
    Boolean(preview.domain) &&
    preview.domain.toLowerCase() !== (preview.title ?? '').toLowerCase();
  const showSkeleton = hasImage && !loaded;

  const openExternal = () => {
    window.open(preview.url, '_blank', 'noopener,noreferrer');
  };

  const handleClick = () => {
    if (showSkeleton) return;

    if (preview.embedUrl) {
      openLightbox({
        kind: 'embed',
        embedUrl: preview.embedUrl,
        title: preview.title ?? preview.domain,
      });
      return;
    }
    openExternal();
  };

  return (
    <button
      type="button"
      className={[
        'teo-rich-media__link-card',
        showSkeleton ? 'teo-rich-media__link-card--loading' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      onClick={handleClick}
    >
      {hasImage && (
        <span
          className={[
            'teo-rich-media__link-card-media',
            showSkeleton ? 'teo-media-skeleton' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <img
            src={preview.imageUrl}
            alt=""
            loading="lazy"
            className="teo-rich-media__link-card-image"
            onLoad={() => {
              setLoaded(true);
              onMediaLoad?.();
            }}
          />
          {isEmbeddable && !showSkeleton && (
            <span className="teo-rich-media__play teo-rich-media__play--sm">
              ▶
            </span>
          )}
        </span>
      )}
      <span className="teo-rich-media__link-card-body">
        {showSkeleton ? (
          <LinkPreviewSkeletonBody
            preview={preview}
            showDescription={showDescription}
            showDomain={showDomain}
          />
        ) : (
          <>
            {preview.siteName && (
              <span className="teo-rich-media__link-card-site">
                {preview.siteName}
              </span>
            )}
            <span className="teo-rich-media__link-card-title">
              {preview.title ?? preview.domain}
            </span>
            {showDescription && (
              <span className="teo-rich-media__link-card-desc">
                {preview.description}
              </span>
            )}
            {showDomain && (
              <span className="teo-rich-media__link-card-domain">
                {preview.domain}
              </span>
            )}
          </>
        )}
      </span>
    </button>
  );
}
